import { useMutation } from '@tanstack/react-query';
import { FC, useState } from 'react';

import dayjs from 'dayjs';

import API from '@api/API';

import Button from '@components/buttons/Button';
import { DatePickerV2 } from '@components/form/DatePicker';
import { Input } from '@components/form/Input';
import { Textarea } from '@components/form/Textarea';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const useSendCircleInquiryMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      socialCircleId,
      title,
      message,
      eventId,
      eventDate,
    }: {
      socialCircleId: string;
      title?: string;
      message: string;
      eventId?: string;
      eventDate?: string;
    }) =>
      API.post(`/v1/user/circles/${socialCircleId}/inquiries`, {
        title,
        message,
        eventId,
        eventDate,
      }),
  });

  return {
    isSendingInquiry: isPending,
    sendInquiryAsync: mutateAsync,
  };
};

interface PopupContentProps {
  socialCircleId: string;
  eventId?: string;
  eventDate?: string;
  onSubmit?: () => void;
  onClose: () => void;
}

const PopupContent: FC<PopupContentProps> = ({
  socialCircleId,
  eventId,
  eventDate: initialEventDate,
  onSubmit,
  onClose,
}) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [eventDate, setEventDate] = useState(initialEventDate ?? '');
  const [error, setError] = useState<string | undefined>();

  const { isSendingInquiry, sendInquiryAsync } = useSendCircleInquiryMutation();

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('A message is required.');
      return;
    }
    if (!eventId && !eventDate) {
      setError('Please provide a date for the outing.');
      return;
    }

    await sendInquiryAsync({
      socialCircleId,
      title: title.trim() || undefined,
      message: message.trim(),
      eventId,
      eventDate: eventDate || undefined,
    });

    onSubmit?.();
    onClose();
  };

  return (
    <div>
      <TitleToolbar text="Put out a feeler" size="sm" />
      <p className="mt-0">
        Let your circle know what you're interested in and see who's down to join.
      </p>

      <Input
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="mb-2"
      />

      {!eventId && (
        <DatePickerV2
          value={eventDate}
          onChange={(date) => setEventDate(dayjs(date).format('YYYY-MM-DD'))}
        />
      )}

      <div className="mt-2">
        <Textarea
          placeholder="What are you thinking? Share the details..."
          onChange={(value) => {
            setError(undefined);
            setMessage(value);
          }}
        />
      </div>

      {error && <p className="text-danger mt-1 mb-0">{error}</p>}

      <Button className="mt-2" disabled={isSendingInquiry} onClick={handleSubmit}>
        Send to circle
      </Button>
    </div>
  );
};

interface SocialCircleInquiryPopupProps extends PopupContentProps {
  isVisible: boolean;
}

export const SocialCircleInquiryPopup: FC<SocialCircleInquiryPopupProps> = ({
  isVisible,
  ...props
}) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose}>
      <Popup.Content>{isVisible && <PopupContent {...props} />}</Popup.Content>
    </Popup>
  );
};
